// Pure helpers behind the Sunday School screen: record numbering, year status,
// and the attendance / offering arithmetic the Summary tab reports. Nothing in
// here touches React or the API, so every rule has a test.

/**
 * The next `PREFIX-NNNN` for a table, numbered past the highest one already
 * issued — voided rows included. Counting live rows (the old rule) re-issued a
 * number as soon as one row was voided, so two registers shared an id.
 * Numbering starts at 1000; rows with ids in another shape (demo data) are
 * ignored rather than trusted.
 */
export const nextRecordId = (
  prefix: string,
  rows: Array<{ recordId?: string | null }>,
  base = 1000
): string => {
  const pattern = new RegExp(`^${prefix}-(\\d+)$`);
  let highest = base - 1;
  rows.forEach((r) => {
    const m = pattern.exec(String(r.recordId || ''));
    if (m) highest = Math.max(highest, parseInt(m[1], 10));
  });
  return `${prefix}-${highest + 1}`;
};

/**
 * Years were saved as "Active", "ACTIVE" and "Archived" over time. The screen
 * compares one spelling, so everything is read as that spelling; anything
 * unrecognised is treated as archived rather than as a second active year.
 */
export const normalizeYearStatus = (status: unknown): 'Active' | 'Archived' =>
  /^active$/i.test(String(status || '').trim()) ? 'Active' : 'Archived';

/** The void stamp every other module writes, so the audit columns match. */
export const voidStamp = (): string =>
  new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * A mark is out of range when it is negative or above the exam total. Blank
 * is allowed — a student who did not sit the exam has no mark, not a zero.
 */
export const markProblem = (obtained: unknown, totalMarks: unknown): string | null => {
  const raw = String(obtained ?? '').trim();
  if (raw === '') return null;
  const n = Number(raw);
  const total = Number(totalMarks);
  if (!Number.isFinite(n)) return 'is not a number';
  if (n < 0) return 'cannot be negative';
  if (Number.isFinite(total) && total > 0 && n > total) return `is above the total of ${total}`;
  return null;
};

// --- Attendance arithmetic ---------------------------------------------------

export interface RegisterLike {
  classId?: string;
  date?: string;
  attendance?: Record<string, boolean>;
  offering?: unknown;
}

export interface AttendanceRate {
  present: number;
  marked: number;
  /** 0-100, or null when nothing has been marked yet. */
  pct: number | null;
}

const rate = (present: number, marked: number): AttendanceRate => ({
  present, marked, pct: marked > 0 ? Math.round((present / marked) * 1000) / 10 : null
});

/** Sheets that actually recorded anyone — offering-only rows are not registers. */
const sheetsOnly = (registers: RegisterLike[]) =>
  registers.filter((r) => Object.keys(r.attendance || {}).length > 0);

/**
 * How often a class turned up, weighted by heads rather than by Sunday: a
 * register of 20 counts twenty times more than a register of 1.
 */
export const classAttendance = (registers: RegisterLike[], classId: string): AttendanceRate => {
  let present = 0;
  let marked = 0;
  sheetsOnly(registers).filter((r) => r.classId === classId).forEach((r) => {
    Object.values(r.attendance || {}).forEach((v) => { marked += 1; if (v) present += 1; });
  });
  return rate(present, marked);
};

/**
 * One student's record across every sheet that lists them. A sheet saved
 * before the student joined does not list them, so it neither counts against
 * them nor for them.
 */
export const studentAttendance = (registers: RegisterLike[], studentId: string): AttendanceRate => {
  let present = 0;
  let marked = 0;
  sheetsOnly(registers).forEach((r) => {
    const v = r.attendance?.[studentId];
    if (v === undefined) return;
    marked += 1;
    if (v) present += 1;
  });
  return rate(present, marked);
};

/** Overall rate across a set of registers, weighted by heads. */
export const overallAttendance = (registers: RegisterLike[]): AttendanceRate => {
  let present = 0;
  let marked = 0;
  sheetsOnly(registers).forEach((r) => {
    Object.values(r.attendance || {}).forEach((v) => { marked += 1; if (v) present += 1; });
  });
  return rate(present, marked);
};

/** Below this a student is flagged for a follow-up with the parent. */
export const LOW_ATTENDANCE_PCT = 60;

// --- Offerings ---------------------------------------------------------------

export interface OfferingLike {
  date?: string;
  classId?: string;
  offering?: unknown;
}

export const amountOf = (row: OfferingLike): number => parseFloat(String(row.offering ?? 0)) || 0;

export const sumOfferings = (rows: OfferingLike[]): number =>
  rows.reduce((s, r) => s + amountOf(r), 0);

export interface MonthTotal {
  /** `YYYY-MM` */
  month: string;
  total: number;
}

/** Totals by calendar month, oldest first. Undated rows are skipped. */
export const offeringsByMonth = (rows: OfferingLike[]): MonthTotal[] => {
  const totals = new Map<string, number>();
  rows.forEach((r) => {
    const month = String(r.date || '').slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    totals.set(month, (totals.get(month) || 0) + amountOf(r));
  });
  return Array.from(totals.entries())
    .map(([month, total]) => ({ month, total }))
    .sort((a, b) => a.month.localeCompare(b.month));
};

/** `2026-03` -> `Mar 2026`. */
export const monthLabel = (month: string): string => {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

// --- Grades and sections -----------------------------------------------------

export interface GradeLike {
  id?: string;
  name?: string;
  academicYearId?: string;
  ageFrom?: unknown;
  ageTo?: unknown;
  location?: string;
}

export interface SectionLike {
  id?: string;
  className?: string;
  academicYearId?: string;
  gradeId?: string;
  section?: string;
  location?: string;
}

/**
 * Age in completed years on a given date. The Sunday School measures age at
 * the academic year's start, so a child does not change grade on a birthday
 * in the middle of the year. Null when either date is unusable.
 */
export const ageOn = (dob: unknown, asOf: unknown): number | null => {
  const born = new Date(String(dob || ''));
  const at = new Date(String(asOf || ''));
  if (isNaN(born.getTime()) || isNaN(at.getTime())) return null;
  let age = at.getFullYear() - born.getFullYear();
  const m = at.getMonth() - born.getMonth();
  if (m < 0 || (m === 0 && at.getDate() < born.getDate())) age -= 1;
  return age < 0 ? null : age;
};

/** Whether an age sits inside a grade's inclusive band. Open ends are allowed. */
export const inAgeBand = (age: number | null, grade: GradeLike): boolean => {
  if (age === null) return false;
  const from = grade.ageFrom === '' || grade.ageFrom == null ? null : Number(grade.ageFrom);
  const to = grade.ageTo === '' || grade.ageTo == null ? null : Number(grade.ageTo);
  if (from !== null && Number.isFinite(from) && age < from) return false;
  if (to !== null && Number.isFinite(to) && age > to) return false;
  return true;
};

/** Two grades in the same year whose age bands touch — a child would fit both. */
export const ageBandsOverlap = (a: GradeLike, b: GradeLike): boolean => {
  const lo = (g: GradeLike) => (g.ageFrom === '' || g.ageFrom == null ? -Infinity : Number(g.ageFrom));
  const hi = (g: GradeLike) => (g.ageTo === '' || g.ageTo == null ? Infinity : Number(g.ageTo));
  return lo(a) <= hi(b) && lo(b) <= hi(a);
};

/** "A", "B", ... "Z", then "AA", "AB" — enough for any parish. */
export const sectionLetter = (index: number): string => {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
};

/** The next free letters after the ones a grade already has. */
export const nextSectionLetters = (existing: string[], count: number): string[] => {
  const taken = new Set(existing.map((s) => String(s || '').toUpperCase()));
  const out: string[] = [];
  for (let i = 0; out.length < count && i < 1000; i += 1) {
    const letter = sectionLetter(i);
    if (!taken.has(letter)) out.push(letter);
  }
  return out;
};

/** The label a section is known by on every dropdown: "Grade 1 – A". */
export const sectionLabel = (section: SectionLike, grade?: GradeLike | null): string => {
  if (!grade) return section.className || section.id || '';
  return section.section ? `${grade.name} – ${section.section}` : String(grade.name || '');
};

/**
 * Hands each newcomer to the section with the fewest students, so a grade
 * fills evenly and a third section does not start empty because the first two
 * happened to be picked. Returns `{ studentKey: sectionId }`.
 */
export const spreadEvenly = <S extends { id?: string }>(
  newcomers: string[],
  sections: S[],
  currentCounts: Record<string, number>
): Record<string, string> => {
  const counts = new Map<string, number>();
  sections.forEach((s) => counts.set(String(s.id), currentCounts[String(s.id)] || 0));
  const out: Record<string, string> = {};
  if (sections.length === 0) return out;
  newcomers.forEach((key) => {
    let best: string | null = null;
    counts.forEach((n, id) => {
      if (best === null || n < (counts.get(best) as number)) best = id;
    });
    out[key] = best as string;
    counts.set(best as string, (counts.get(best as string) || 0) + 1);
  });
  return out;
};

export interface GradeGroup<G extends GradeLike, S extends SectionLike> {
  /** The grade, or a stand-in built from a pre-grade class. */
  grade: G | (GradeLike & { id: string; synthetic: true });
  sections: S[];
}

/**
 * Grades with their sections, for the card view. A class saved before grades
 * existed has no gradeId; it is shown as a one-section grade of its own,
 * named after the class, so nothing recorded against it goes missing.
 */
export const groupSections = <G extends GradeLike, S extends SectionLike>(
  grades: G[],
  sections: S[]
): GradeGroup<G, S>[] => {
  const byGrade = new Map<string, GradeGroup<G, S>>();
  grades.forEach((g) => byGrade.set(String(g.id), { grade: g, sections: [] }));
  const orphans: GradeGroup<G, S>[] = [];
  sections.forEach((s) => {
    const group = s.gradeId ? byGrade.get(s.gradeId) : undefined;
    if (group) {
      group.sections.push(s);
    } else {
      orphans.push({
        grade: { id: String(s.id), name: s.className, academicYearId: s.academicYearId, location: s.location, synthetic: true },
        sections: [s]
      });
    }
  });
  const letter = (s: SectionLike) => String(s.section || s.className || '');
  byGrade.forEach((g) => g.sections.sort((a, b) => letter(a).localeCompare(letter(b))));
  const named = (x: GradeGroup<G, S>) => String(x.grade.name || '');
  return [...Array.from(byGrade.values()), ...orphans]
    .sort((a, b) => named(a).localeCompare(named(b), undefined, { numeric: true }));
};

// --- Calendar ----------------------------------------------------------------

/** ISO date of the most recent Sunday on or before `today` (default: now). */
export const lastSunday = (today: Date = new Date()): string => {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  d.setDate(d.getDate() - d.getDay());
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Whether an ISO date falls on a Sunday. Bad dates are not Sundays. */
/**
 * The Sunday `weeks` either side of `iso`, for the arrows beside the Sunday
 * picker (CHM-133). Anchors to that week's Sunday first, so stepping from a
 * mid-week date still lands on a Sunday.
 */
export const shiftSunday = (iso: string, weeks: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date();
  d.setDate(d.getDate() - d.getDay() + weeks * 7);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const isSunday = (iso: unknown): boolean => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return false;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay() === 0;
};

// --- Exams -------------------------------------------------------------------

export interface ExamLike {
  id?: string;
  academicYearId?: string;
  gradeId?: string;
  /** Set on exams created before school-wide exams existed: one section only. */
  classId?: string;
}

/**
 * Whether a section sits this exam. A school-wide exam applies to every
 * section of its year, or of one grade when a grade is named; a legacy exam
 * applies to the one section it was created for.
 */
export const examAppliesTo = (exam: ExamLike, section: SectionLike): boolean => {
  if (exam.classId) return exam.classId === section.id;
  if (exam.academicYearId && exam.academicYearId !== section.academicYearId) return false;
  if (exam.gradeId && exam.gradeId !== section.gradeId) return false;
  return Boolean(exam.academicYearId || exam.gradeId);
};

export const examSections = <S extends SectionLike>(exam: ExamLike, sections: S[]): S[] =>
  sections.filter((s) => examAppliesTo(exam, s));

export interface MarksRowLike {
  examId?: string;
  classId?: string;
  scores?: Record<string, { obtained?: unknown }>;
}

/**
 * The saved scores for one exam in one section. A legacy row has no classId
 * and belongs to the (per-section) exam's own section.
 */
export const marksFor = <M extends MarksRowLike>(rows: M[], exam: ExamLike, classId: string): M | undefined =>
  rows.find((m) => m.examId === exam.id && (m.classId ? m.classId === classId : exam.classId === classId));

/** How many students in the row have a mark entered. */
export const marksEntered = (row?: MarksRowLike): number =>
  Object.values(row?.scores || {}).filter((s) => String(s?.obtained ?? '').trim() !== '').length;

// --- Results -----------------------------------------------------------------

export type ExamResult = 'PASS' | 'FAIL' | 'ABSENT';

/** Default pass mark when an exam has none: 40% of the total. */
export const DEFAULT_PASS_PCT = 40;

/** The marks needed to pass this exam, from its own pass mark or the default share of the total. */
export const passMarkFor = (exam: { passMarks?: unknown; totalMarks?: unknown }): number => {
  const explicit = Number(exam.passMarks);
  if (exam.passMarks !== '' && exam.passMarks != null && Number.isFinite(explicit)) return explicit;
  const total = Number(exam.totalMarks) || 0;
  return Math.round((total * DEFAULT_PASS_PCT) / 100 * 100) / 100;
};

/**
 * Pass, fail or absent. A blank mark is absent — the student did not sit —
 * never a fail; zero is a fail.
 */
export const resultOf = (obtained: unknown, exam: { passMarks?: unknown; totalMarks?: unknown }): ExamResult => {
  const raw = String(obtained ?? '').trim();
  if (raw === '') return 'ABSENT';
  const n = Number(raw);
  if (!Number.isFinite(n)) return 'ABSENT';
  return n >= passMarkFor(exam) ? 'PASS' : 'FAIL';
};

export interface ResultTally { passed: number; failed: number; absent: number; sat: number; passPct: number | null }

/** Tally of one section's sheet. Students not on the sheet at all count as absent. */
export const tallyResults = (
  studentIds: string[],
  scores: Record<string, { obtained?: unknown }> | undefined,
  exam: { passMarks?: unknown; totalMarks?: unknown }
): ResultTally => {
  let passed = 0; let failed = 0; let absent = 0;
  studentIds.forEach((id) => {
    const r = resultOf(scores?.[id]?.obtained, exam);
    if (r === 'PASS') passed += 1; else if (r === 'FAIL') failed += 1; else absent += 1;
  });
  const sat = passed + failed;
  return { passed, failed, absent, sat, passPct: sat > 0 ? Math.round((passed / sat) * 1000) / 10 : null };
};

/** The band a report card prints, given an overall percentage and the exam's pass share. */
export const gradeBand = (overallPct: number, passPct: number): string =>
  overallPct >= 80 ? 'Distinction'
    : overallPct >= 60 ? 'First Class'
      : overallPct >= passPct ? 'Pass'
        : 'Fail';
